/**
 * Admin Quests page
 *
 * Pragmatic CRUD: list view + inline create/edit modal. Lets admin:
 *   - Toggle active/inactive
 *   - Edit title, description, criteria JSON, coin amount, scope, dates
 *   - See completion counts at a glance
 *   - Create new quests scoped to global / main event / undercard
 *
 * Heavier polish (search, filters, audit trail) deferred to Phase 2.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Pencil, Save, X, Trash2, Power } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Stat } from '@/components/ui/stat';
import { authFetch } from '@/lib/auth-client';

// labels mirror quest-spec.ts in the nextjs app; a deprecated type is offered
// only while editing a quest that already uses it (selectableSpecs behavior)
const QUEST_TYPES: { value: string; label: string; deprecated?: boolean }[] = [
  { value: 'ENTER_EVENT', label: 'Enter an event' },
  { value: 'PICKS_ACCURACY', label: 'Get N correct picks in a round' },
  { value: 'PERFECT_PICKS', label: 'Perfect round (every pick correct)' },
  { value: 'FINISH_TOP_N', label: 'Finish in the top N of an event' },
  { value: 'PROP_PURCHASE_COUNT', label: 'Buy N props in a round' },
  { value: 'WIN_UNDERCARD', label: 'Win a round' },
  { value: 'COIN_SPEND_LIFETIME', label: 'Spend N coins (lifetime)' },
  { value: 'COIN_SPEND_EVENT', label: 'Spend N coins in this event' },
  { value: 'COIN_SPEND_WINDOW', label: 'Spend N coins in the last N days' },
  { value: 'COIN_SPEND', label: 'Spend N coins (legacy)', deprecated: true },
  { value: 'TOTAL_EVENTS_ENTERED', label: 'Enter N events (lifetime)' },
  { value: 'REFERRAL_COUNT', label: 'Refer N players' },
  { value: 'DAILY_LOGIN_STREAK', label: 'Keep a daily login streak' },
  { value: 'CUSTOM', label: 'Custom (manual award only)' },
];

const CRITERIA_HINTS: Record<string, string> = {
  PICKS_ACCURACY: '{"minCorrect": 5}',
  FINISH_TOP_N: '{"rank": 10}',
  DAILY_LOGIN_STREAK: '{"days": 7}, optionally {"useLongest": true}',
  COIN_SPEND_LIFETIME: '{"coinsSpent": 1000}',
  COIN_SPEND_EVENT: '{"coinsSpent": 500}',
  COIN_SPEND_WINDOW: '{"coinsSpent": 500, "days": 7}',
};

interface Quest {
  id: string;
  mainEventId: string | null;
  undercardId: string | null;
  title: string;
  description: string;
  iconKey: string | null;
  questType: string;
  criteria: Record<string, unknown>;
  coinsAmount: number;
  startsAt: string | null;
  expiresAt: string | null;
  maxClaimsPerUser: number;
  maxClaimsTotal: number | null;
  isActive: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  _count: { completions: number };
  mainEvent: { id: string; title: string } | null;
  undercard: { id: string; title: string } | null;
}

const EMPTY_DRAFT = {
  id: null as string | null,
  mainEventId: '',
  undercardId: '',
  title: '',
  description: '',
  iconKey: '',
  questType: 'ENTER_EVENT',
  criteriaJson: '{}',
  coinsAmount: 50,
  startsAt: '',
  expiresAt: '',
  maxClaimsPerUser: 1,
  maxClaimsTotal: '',
  isActive: true,
};

type Draft = typeof EMPTY_DRAFT;

export default function AdminQuestsPage() {
  const [quests, setQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/quests');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setQuests((body.data?.quests ?? body.quests ?? []) as Quest[]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load quests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleNew = () => {
    setError(null);
    setDraft({ ...EMPTY_DRAFT });
  };
  const handleEdit = (q: Quest) => {
    setError(null);
    setDraft({
      id: q.id,
      mainEventId: q.mainEventId ?? '',
      undercardId: q.undercardId ?? '',
      title: q.title,
      description: q.description,
      iconKey: q.iconKey ?? '',
      questType: q.questType,
      criteriaJson: JSON.stringify(q.criteria ?? {}, null, 2),
      coinsAmount: q.coinsAmount,
      startsAt: q.startsAt ? q.startsAt.slice(0, 16) : '',
      expiresAt: q.expiresAt ? q.expiresAt.slice(0, 16) : '',
      maxClaimsPerUser: q.maxClaimsPerUser,
      maxClaimsTotal: q.maxClaimsTotal === null ? '' : String(q.maxClaimsTotal),
      isActive: q.isActive,
    });
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      let parsedCriteria: Record<string, unknown> = {};
      try {
        parsedCriteria = draft.criteriaJson.trim() ? JSON.parse(draft.criteriaJson) : {};
        if (typeof parsedCriteria !== 'object' || Array.isArray(parsedCriteria)) {
          throw new Error('criteria must be an object');
        }
      } catch {
        setError('Criteria JSON is invalid');
        setSaving(false);
        return;
      }

      const payload = {
        mainEventId: draft.mainEventId.trim() || null,
        undercardId: draft.undercardId.trim() || null,
        title: draft.title.trim(),
        description: draft.description.trim(),
        iconKey: draft.iconKey.trim() || null,
        questType: draft.questType,
        criteria: parsedCriteria,
        coinsAmount: draft.coinsAmount,
        startsAt: draft.startsAt ? new Date(draft.startsAt).toISOString() : null,
        expiresAt: draft.expiresAt ? new Date(draft.expiresAt).toISOString() : null,
        maxClaimsPerUser: draft.maxClaimsPerUser,
        maxClaimsTotal: draft.maxClaimsTotal === '' ? null : Number(draft.maxClaimsTotal),
        isActive: draft.isActive,
      };

      const url = draft.id ? `/api/admin/quests/${draft.id}` : '/api/admin/quests';
      const method = draft.id ? 'PUT' : 'POST';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setDraft(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (q: Quest) => {
    try {
      await authFetch(`/api/admin/quests/${q.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !q.isActive }),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toggle failed');
    }
  };

  const handleDelete = async (q: Quest) => {
    if (!confirm(`Delete quest "${q.title}"? This cascades to ${q._count.completions} completion record(s).`)) return;
    try {
      await authFetch(`/api/admin/quests/${q.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const stats = useMemo(() => {
    const total = quests.length;
    const active = quests.filter((q) => q.isActive).length;
    const completions = quests.reduce((sum, q) => sum + q._count.completions, 0);
    const coinsAwarded = quests.reduce((sum, q) => sum + q.coinsAmount * q._count.completions, 0);
    return { total, active, completions, coinsAwarded };
  }, [quests]);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-cream">Quests</h1>
          <p className="text-xs text-warm-gray/50 mt-0.5">
            Event-scoped or global achievements that auto-award on trigger.
          </p>
        </div>
        <button
          onClick={handleNew}
          className="px-3 py-1.5 rounded-lg bg-gold/15 text-gold border border-gold/30 hover:bg-gold/25 text-xs font-bold inline-flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" /> New Quest
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <Stat variant="dense" label="Total quests" value={stats.total} />
        <Stat variant="dense" label="Active" value={stats.active} />
        <Stat variant="dense" label="Completions" value={stats.completions} />
        <Stat variant="dense" label="Coins awarded" value={`${stats.coinsAwarded.toLocaleString()}c`} />
      </div>

      {error && (
        <div className="rounded-lg bg-oxblood/10 border border-oxblood/30 text-oxblood text-xs p-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-warm-gray/60 text-xs"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
      ) : quests.length === 0 ? (
        <div className="rounded-lg border border-surface-3 bg-surface-2 p-6 text-center text-xs text-warm-gray/50">
          No quests yet. Click &quot;New Quest&quot; to create one.
        </div>
      ) : (
        <div className="rounded-lg border border-surface-3 bg-surface-2 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-surface-3/50 text-[10px] text-warm-gray/60 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Quest</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Scope</th>
                <th className="text-right px-3 py-2">Coins</th>
                <th className="text-right px-3 py-2">Completions</th>
                <th className="text-right px-3 py-2">Active</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {quests.map((q) => (
                <tr key={q.id} className="border-t border-surface-3/50 hover:bg-surface-3/20">
                  <td className="px-3 py-2">
                    <div className="font-bold text-cream">{q.title}</div>
                    <div className="text-[10px] text-warm-gray/50 truncate max-w-md">{q.description}</div>
                  </td>
                  <td className="px-3 py-2 text-warm-gray/70 font-mono text-[10px]">{q.questType}</td>
                  <td className="px-3 py-2 text-warm-gray/60 text-[10px]">
                    {q.undercard ? (
                      <span className="text-verdigris">Undercard: {q.undercard.title}</span>
                    ) : q.mainEvent ? (
                      <span className="text-gold">Event: {q.mainEvent.title}</span>
                    ) : (
                      <span>Global</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-gold font-bold">{q.coinsAmount}</td>
                  <td className="px-3 py-2 text-right text-warm-gray/70">{q._count.completions}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => handleToggleActive(q)}
                      className={cn(
                        'inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-[10px] font-bold',
                        q.isActive
                          ? 'bg-verdigris/15 text-verdigris border border-verdigris/30'
                          : 'bg-surface-3 text-warm-gray/40 border border-surface-4',
                      )}
                    >
                      <Power className="w-2.5 h-2.5" />
                      {q.isActive ? 'ON' : 'OFF'}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => handleEdit(q)} className="p-1 rounded hover:bg-surface-4 text-warm-gray/70 hover:text-cream">
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button onClick={() => handleDelete(q)} className="p-1 rounded hover:bg-oxblood/20 text-warm-gray/70 hover:text-oxblood">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {draft && (
        <DraftModal
          draft={draft}
          setDraft={setDraft}
          saving={saving}
          error={error}
          onCancel={() => setDraft(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function DraftModal({
  draft,
  setDraft,
  saving,
  error,
  onCancel,
  onSave,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-xl bg-surface-2 border border-surface-3 max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-surface-3 flex items-center justify-between sticky top-0 bg-surface-2 z-10">
          <h2 className="text-sm font-bold text-cream">{draft.id ? 'Edit quest' : 'New quest'}</h2>
          <button onClick={onCancel} disabled={saving} className="text-warm-gray/60 hover:text-cream">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3 text-xs">
          {error && (
            <div className="rounded-lg bg-oxblood/10 border border-oxblood/30 text-oxblood text-xs p-3">
              {error}
            </div>
          )}

          <Field label="Title">
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              maxLength={200}
              className={inputCx}
            />
          </Field>
          <Field label="Description">
            <textarea
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              maxLength={1000}
              rows={2}
              className={inputCx}
            />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Quest type">
              <select
                value={draft.questType}
                onChange={(e) => setDraft({ ...draft, questType: e.target.value })}
                className={inputCx}
              >
                {QUEST_TYPES.filter((t) => !t.deprecated || t.value === draft.questType).map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Coin reward">
              <input
                type="number"
                value={draft.coinsAmount}
                min={0}
                onChange={(e) => setDraft({ ...draft, coinsAmount: Number(e.target.value) || 0 })}
                className={inputCx}
              />
            </Field>
          </div>

          <Field label="Criteria (JSON)">
            <textarea
              value={draft.criteriaJson}
              onChange={(e) => setDraft({ ...draft, criteriaJson: e.target.value })}
              rows={4}
              className={cn(inputCx, 'font-mono')}
              spellCheck={false}
            />
            <p className="text-[10px] text-warm-gray/40 mt-1">
              e.g. <code>{CRITERIA_HINTS[draft.questType] ?? '{}'}</code> for {draft.questType}
            </p>
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Main event ID (optional)">
              <input
                value={draft.mainEventId}
                onChange={(e) => setDraft({ ...draft, mainEventId: e.target.value })}
                placeholder="leave empty for global"
                className={inputCx}
              />
            </Field>
            <Field label="Undercard ID (optional)">
              <input
                value={draft.undercardId}
                onChange={(e) => setDraft({ ...draft, undercardId: e.target.value })}
                placeholder="leave empty for global"
                className={inputCx}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Starts at (optional)">
              <input
                type="datetime-local"
                value={draft.startsAt}
                onChange={(e) => setDraft({ ...draft, startsAt: e.target.value })}
                className={inputCx}
              />
            </Field>
            <Field label="Expires at (optional)">
              <input
                type="datetime-local"
                value={draft.expiresAt}
                onChange={(e) => setDraft({ ...draft, expiresAt: e.target.value })}
                className={inputCx}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Max claims per user">
              <input
                type="number"
                value={draft.maxClaimsPerUser}
                min={1}
                onChange={(e) => setDraft({ ...draft, maxClaimsPerUser: Number(e.target.value) || 1 })}
                className={inputCx}
              />
            </Field>
            <Field label="Total payout cap (optional)">
              <input
                value={draft.maxClaimsTotal}
                onChange={(e) => setDraft({ ...draft, maxClaimsTotal: e.target.value })}
                placeholder="empty = unlimited"
                className={inputCx}
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-cream">
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
            />
            Active
          </label>
        </div>

        <div className="px-4 py-3 border-t border-surface-3 flex justify-end gap-2">
          <button onClick={onCancel} disabled={saving} className="px-3 py-1.5 rounded text-xs text-warm-gray hover:text-cream">Cancel</button>
          <button
            onClick={onSave}
            disabled={saving || !draft.title.trim() || !draft.description.trim()}
            className="px-3 py-1.5 rounded text-xs font-bold bg-gold/15 text-gold border border-gold/30 hover:bg-gold/25 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCx = 'w-full bg-surface-3 border border-surface-4 rounded px-2 py-1.5 text-xs text-cream focus:outline-none focus:ring-2 focus:ring-gold/40';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[10px] text-warm-gray/60 uppercase tracking-wider mb-1 block">{label}</span>
      {children}
    </label>
  );
}
